import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { RegistrationState, Complete } from '../../models/registration.model';
import { RegistrationCommentService } from '../../services/registration-comment.service';
import { setComplete, saveRegistration } from '../../store/actions/complete.actions';
import { hasBatches, selectBatches } from '../../store/selectors/batch.selectors';
import { selectComplete } from '../../store/selectors/complete.selectors';
import {
  selectInformation,
  hasValidInformation,
} from '../../store/selectors/information.selectors';

@Component({
  selector: 'app-registration-complete',
  templateUrl: './complete.component.html',
  styleUrls: ['./complete.component.scss'],
  standalone: false,
})
export class CompleteComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject<Store<RegistrationState>>(Store);
  private readonly registrationCommentService = inject(RegistrationCommentService);
  mandatoryCommentPrefix = '';

  completeForm = new FormGroup({
    comment: new FormControl('', []),
    requirementsAccepted: new FormControl(null, [Validators.required, Validators.requiredTrue]),
  });

  completeForm$ = this.store.select(selectComplete);
  hasValidInformationForm$ = this.store.select(hasValidInformation);
  hasBatches$ = this.store.select(hasBatches);
  informationForm$ = this.store.select(selectInformation);

  constructor() {
    this.store
      .select(selectBatches)
      .pipe(
        map((batches) => this.registrationCommentService.buildMandatoryComment(batches)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((prefix) => {
        this.mandatoryCommentPrefix = prefix;
      });
  }

  getDisplayedComment(): string {
    const comment = this.completeForm.controls.comment.value || '';
    const mandatoryComment = this.getMandatoryComment();

    if (!mandatoryComment) {
      return comment;
    }

    return comment ? `${mandatoryComment} ${comment}` : mandatoryComment;
  }

  onCommentInput(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    const rawComment = this.extractEditableComment(textarea.value);

    this.completeForm.controls.comment.setValue(rawComment, { emitEvent: false });
    textarea.value = this.getDisplayedComment();
    this.moveCursorToTextEnd(textarea);
    this.setComplete();
  }

  protectMandatoryComment(event: KeyboardEvent) {
    if (!this.mandatoryCommentPrefix || !this.isContentChangingKey(event)) {
      return;
    }

    const textarea = event.target as HTMLTextAreaElement;
    const protectedEnd = this.getProtectedCommentEnd();
    const selectionStart = textarea.selectionStart ?? 0;
    const selectionEnd = textarea.selectionEnd ?? 0;

    if (selectionStart < protectedEnd || selectionEnd < protectedEnd) {
      event.preventDefault();
      this.moveCursorToTextEnd(textarea);
    }
  }

  moveCursorBeforeMandatoryComment(event: Event) {
    if (!this.mandatoryCommentPrefix) {
      return;
    }

    const textarea = event.target as HTMLTextAreaElement;
    Promise.resolve().then(() => this.moveCursorAfterMandatoryComment(textarea));
  }

  setComplete() {
    this.store.dispatch(setComplete({ complete: this.completeForm.value as unknown as Complete }));
  }

  save() {
    this.completeForm.controls.requirementsAccepted.markAsTouched();

    if (this.completeForm.valid) {
      this.store.dispatch(saveRegistration());
    }
  }

  private getMandatoryComment(): string {
    return this.mandatoryCommentPrefix;
  }

  private extractEditableComment(comment: string): string {
    const mandatoryComment = this.getMandatoryComment();

    if (!mandatoryComment) {
      return comment;
    }

    const normalizedComment = comment.replaceAll(mandatoryComment, '');
    const mandatoryPrefixLength = this.getMandatoryPrefixMatchLength(comment, mandatoryComment);

    if (mandatoryPrefixLength > 0) {
      return comment.slice(mandatoryPrefixLength).trimStart();
    }

    return normalizedComment.trimStart();
  }

  private getProtectedCommentEnd(): number {
    return this.getMandatoryComment().length;
  }

  private moveCursorAfterMandatoryComment(textarea: HTMLTextAreaElement) {
    const comment = this.completeForm.controls.comment.value || '';
    const cursorPosition = this.getMandatoryComment().length + (comment ? 1 : 0);

    if (
      (textarea.selectionStart ?? 0) < cursorPosition ||
      (textarea.selectionEnd ?? 0) < cursorPosition
    ) {
      textarea.setSelectionRange(cursorPosition, cursorPosition);
    }
  }

  private moveCursorToTextEnd(textarea: HTMLTextAreaElement) {
    const cursorPosition = textarea.value.length;
    textarea.setSelectionRange(cursorPosition, cursorPosition);
  }

  private isContentChangingKey(event: KeyboardEvent): boolean {
    return (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'Enter' ||
      (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey)
    );
  }

  private getMandatoryPrefixMatchLength(comment: string, mandatoryComment: string): number {
    const maxPrefixLength = Math.min(comment.length, mandatoryComment.length);

    for (let index = maxPrefixLength; index > 0; index -= 1) {
      if (mandatoryComment.startsWith(comment.slice(0, index))) {
        return index;
      }
    }

    return 0;
  }
}
